import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { useScrollDepth } from "./hooks/useScrollDepth";
import { trackThemeToggle } from "./utils/analytics";
// SubNavbar and Footer may need to be created if not present
// import SubNavbar from "./components/SubNavbar";
import Home from "./pages/Home";
import Portfolio from "./pages/Portfolio";
import Shop from "./pages/Shop";
import About from "./pages/About";
import Checkout from "./pages/Checkout";
import HelloStickers from "./pages/HelloStickers";
import NotFound from "./pages/NotFound";
import Changelog from "./pages/Changelog";
import ProductPage from "./pages/ProductPage";

// Placeholder components for missing ones
const SubNavbar = () => null;
const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

const PAGE_META = {
  "/": {
    title:
      "Mike Terpeza — Original Art, Paintings, Illustration & Lettering | miketerpeza.com",
    description:
      "Official portfolio and shop of Mike Terpeza (Michael Terpeza) — Midwest visual artist based in Wisconsin. Original paintings, illustrations, calligraphy, hand lettering, graffiti-inspired fine art, photography, and digital art.",
  },
  "/portfolio": {
    title:
      "Portfolio — Mike Terpeza | Paintings, Lettering, Photography & Illustration",
    description:
      "Browse the full portfolio of Mike Terpeza (Michael Terpeza): dot-series & landscape paintings, hand lettering, calligraphy, graffiti-style art, blackbook sketches, photography, MS Paint illustrations, and more.",
  },
  "/about": {
    title: "About — Mike Terpeza | Visual Artist, Illustrator & Letterer",
    description:
      "Mike Terpeza (Michael Terpeza) is a self-taught visual artist from Wisconsin. Original paintings, hand lettering, graffiti art, calligraphy, illustration, and photography. Available for commission.",
  },
  "/store": {
    title:
      "Shop — Mike Terpeza | Art Stickers, Graphic T-Shirts & Original Art",
    description:
      "Shop original art stickers, graphic T-shirts, paintings, and limited prints by Mike Terpeza (Michael Terpeza), a Wisconsin visual artist. Stickers and shirts are printed and fulfilled by Sticker Mule.",
  },
  "/shop": {
    title:
      "Shop — Mike Terpeza | Art Stickers, Graphic T-Shirts & Original Art",
    description:
      "Shop original art stickers, graphic T-shirts, paintings, and limited prints by Mike Terpeza (Michael Terpeza), a Wisconsin visual artist. Stickers and shirts are printed and fulfilled by Sticker Mule.",
  },
  "/hello-stickers": {
    title: "Hello Stickers — Original Art Stickers by Mike Terpeza",
    description:
      "Hello Stickers: vinyl art stickers designed and illustrated by Mike Terpeza (Michael Terpeza). Mix-and-match sets, original hand-lettered and illustrated characters.",
  },
  "/changelog": {
    title: "Changelog — miketerpeza.com | Mike Terpeza",
    description:
      "Site changelog and update history for miketerpeza.com — the official website of artist Mike Terpeza (Michael Terpeza).",
  },
};

const DEFAULT_META = {
  title: "Mike Terpeza Artwork | miketerpeza.com",
  description:
    "Mike Terpeza (Michael Terpeza) — Midwest artist based in Wisconsin. Original paintings, illustrations, calligraphy, lettering, graffiti art, photography, and digital art for sale.",
};

function RouteScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!gaMeasurementId || typeof window.gtag !== "function") return;
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const baseUrl = "https://www.miketerpeza.com";
    const path = location.pathname === "/" ? "" : location.pathname;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${baseUrl}${path}`);

    // Per-page title, description, and OG tags
    const meta = PAGE_META[location.pathname] || DEFAULT_META;
    document.title = meta.title;
    const descTag = document.querySelector('meta[name="description"]');
    if (descTag) descTag.setAttribute("content", meta.description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", meta.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", meta.description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", `${baseUrl}${path}`);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", meta.title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", meta.description);
  }, [location.pathname]);

  useScrollDepth(location.pathname);

  return null;
}

function App() {
  // Theme state and persistence
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "dark",
  );
  // Green screen easter egg
  const [greenScreen, setGreenScreen] = useState(
    () => localStorage.getItem("greenScreen") === "1",
  );
  // Cart state
  const [cart, setCart] = useState([]);
  // Breadcrumb text surfaced by Portfolio into the Navbar toggle bar
  const [navBreadcrumb, setNavBreadcrumb] = useState(null);
  // Back to Top button
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [backToTopBottom, setBackToTopBottom] = useState(20);
  const footerRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.classList.toggle("green-screen", greenScreen);
    localStorage.setItem("greenScreen", greenScreen ? "1" : "0");
  }, [greenScreen]);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 600);

      const footer = footerRef.current;
      if (!footer) {
        setBackToTopBottom(20);
        return;
      }

      const footerTop = footer.getBoundingClientRect().top;
      const overlap = Math.max(0, window.innerHeight - footerTop);
      const extraOffset = overlap > 0 ? overlap + 12 : 0;
      const isSmallViewport = window.innerWidth < 1024;
      const baseOffset = greenScreen && isSmallViewport ? 76 : 20;
      setBackToTopBottom(baseOffset + extraOffset);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [greenScreen]);

  useEffect(() => {
    const isProtectedTarget = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest("img, canvas"));
    };

    const blockContextMenu = (event) => {
      if (isProtectedTarget(event.target)) {
        event.preventDefault();
      }
    };

    const blockDragStart = (event) => {
      if (isProtectedTarget(event.target)) {
        event.preventDefault();
      }
    };

    const blockSaveShortcuts = (event) => {
      const key = event.key.toLowerCase();
      const isSaveLikeShortcut =
        (event.metaKey || event.ctrlKey) && (key === "s" || key === "p");
      if (isSaveLikeShortcut) {
        event.preventDefault();
      }
    };

    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("dragstart", blockDragStart);
    window.addEventListener("keydown", blockSaveShortcuts);

    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("dragstart", blockDragStart);
      window.removeEventListener("keydown", blockSaveShortcuts);
    };
  }, []);

  useEffect(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.beginPath();
    ctx.arc(32, 32, 24, 0, Math.PI * 2);
    ctx.fillStyle = theme === "dark" ? "#6cebe4" : "#ff4000";
    ctx.fill();

    const dataUrl = canvas.toDataURL("image/png");
    const rels = [
      "icon",
      "shortcut icon",
      "apple-touch-icon",
      "apple-touch-icon-precomposed",
    ];
    rels.forEach((rel) => {
      // Remove all existing links for this rel
      document
        .querySelectorAll(`link[rel='${rel}']`)
        .forEach((el) => el.parentNode?.removeChild(el));
      // Insert a fresh one so browsers re-read it
      const link = document.createElement("link");
      link.rel = rel;
      link.type = "image/png";
      link.href = dataUrl;
      document.head.appendChild(link);
    });
  }, [theme]);

  // GA is loaded via index.html — no dynamic script needed here.
  // The route-change useEffect in RouteTracker handles page_view events.

  const toggleTheme = () =>
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      trackThemeToggle(next);
      return next;
    });
  const toggleGreenScreen = () => setGreenScreen((g) => !g);
  const addToCart = useCallback(
    (product) => setCart((c) => [...c, product]),
    [],
  );
  const removeFromCart = (index) =>
    setCart((c) => c.filter((_, i) => i !== index));
  const clearCart = () => setCart([]);
  const scrollBackToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const backToTopShadow =
    theme === "dark"
      ? "0 0 6px 1.5px #6cebe4, 0 2px 8px 0 rgba(0,0,0,0.10)"
      : "0 2px 8px 0 rgba(0,0,0,0.10)";

  return (
    <div className="fade-in min-h-screen flex flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <RouteScrollManager />
        <Navbar
          cart={cart}
          theme={theme}
          toggleTheme={toggleTheme}
          breadcrumb={navBreadcrumb}
        />
        <SubNavbar theme={theme} toggleTheme={toggleTheme} />
        <div className="site-accent-scope flex-1 flex flex-col">
          <main className="flex-1" id="main-content">
            <Routes>
              <Route path="/" element={<Home theme={theme} />} />
              <Route
                path="/portfolio"
                element={<Portfolio onBreadcrumbChange={setNavBreadcrumb} />}
              />
              <Route path="/about" element={<About theme={theme} />} />
              <Route
                path="/store"
                element={
                  <Shop
                    addToCart={addToCart}
                    onBreadcrumbChange={setNavBreadcrumb}
                  />
                }
              />
              <Route
                path="/shop"
                element={
                  <Shop
                    addToCart={addToCart}
                    onBreadcrumbChange={setNavBreadcrumb}
                  />
                }
              />
              <Route path="/shop/:productId" element={<ProductPage />} />
              <Route path="/hello-stickers" element={<HelloStickers />} />
              <Route
                path="/hello-stickers/:stickerId"
                element={<HelloStickers />}
              />
              <Route path="/changelog" element={<Changelog />} />
              <Route
                path="/cart"
                element={
                  <Checkout
                    cart={cart}
                    removeFromCart={removeFromCart}
                    clearCart={clearCart}
                  />
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <div ref={footerRef}>
            <Footer
              greenScreen={greenScreen}
              toggleGreenScreen={toggleGreenScreen}
            />
          </div>
          {showBackToTop && (
            <button
              type="button"
              onClick={scrollBackToTop}
              className="fixed right-5 z-50 flex items-center justify-center bg-[#ff4000] dark:bg-[#6cebe4] text-white dark:text-gray-900 rounded-full shadow-lg w-12 h-12 dark:border dark:border-white/70 hover:scale-110 hover:brightness-105 transition-all duration-200 group"
              aria-label="Back to Top"
              style={{
                boxShadow: backToTopShadow,
                bottom: `${backToTopBottom}px`,
              }}
            >
              <svg
                className="w-7 h-7 animate-bounce-slow"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
          )}
        </div>
      </Router>
    </div>
  );
}

export default App;
